// src/components/cofounder/CoFounderMessageList.js
import React, { useRef, useEffect } from 'react';
import { Brain, User, Loader } from 'lucide-react';
import MessageRenderer from './MessageRenderer';
import ActionConfirmation from './ActionConfirmation';
import { useCoFounder } from '../../context/CoFounderContext';

const CoFounderMessageList = () => {
  const { messages, isLoading, pendingActions } = useCoFounder();
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const getActionsForMessage = (msgIndex) => {
    // Show pending actions after the last assistant message
    if (msgIndex === messages.length - 1 && messages[msgIndex]?.role === 'assistant') {
      return pendingActions.filter(a => a.status === 'pending' || a.status === 'executing');
    }
    return [];
  };

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#190192] bg-opacity-10 flex items-center justify-center mb-4">
          <Brain size={28} className="text-[#190192]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Hi, I'm your coFounder</h3>
        <p className="text-sm text-gray-500 max-w-xs">
          I can help you analyze your business data, find opportunities, manage tasks, and more. What would you like to work on?
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((msg, idx) => (
        <div key={msg.id || idx}>
          <div className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
              msg.role === 'user'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-[#190192] bg-opacity-10 text-[#190192]'
            }`}>
              {msg.role === 'user' ? <User size={14} /> : <Brain size={14} />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
              msg.role === 'user'
                ? 'bg-[#190192] text-white'
                : msg.isError
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {msg.role === 'user' ? (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              ) : (
                <MessageRenderer content={msg.content} />
              )}
            </div>
          </div>

          {/* Show action confirmations */}
          {getActionsForMessage(idx).map(action => (
            <div key={action.id} className="ml-10 mt-1">
              <ActionConfirmation action={action} />
            </div>
          ))}
        </div>
      ))}

      {isLoading && (
        <div className="flex gap-2.5">
          <div className="w-7 h-7 rounded-full bg-[#190192] bg-opacity-10 flex items-center justify-center flex-shrink-0">
            <Brain size={14} className="text-[#190192]" />
          </div>
          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="flex items-center gap-2">
              <Loader size={14} className="animate-spin text-[#190192]" />
              <span className="text-sm text-gray-500">Thinking...</span>
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default CoFounderMessageList;
